import { __decorate } from "tslib";
import __NG_CLI_RESOURCE__0 from "./access-users-grid.component.html?ngResource";
import { ChangeDetectorRef, Component, Input, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { AccessListService } from '../services/access-list.service';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { Subject, throwError } from 'rxjs';
import { CheckboxModule } from 'primeng/checkbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { RippleModule } from 'primeng/ripple';
import _ from 'lodash';
let AccessUsersGridComponent = class AccessUsersGridComponent {
    data;
    listId;
    listOrg;
    userDialog;
    users;
    selectedUsers;
    userForm;
    multiSortMeta = [
        { field: 'lastName', order: 1 },
        { field: 'firstName', order: 1 }
    ];
    unsubscribe$ = new Subject();
    accessListService = inject(AccessListService);
    messageService = inject(MessageService);
    confirmationService = inject(ConfirmationService);
    cdr = inject(ChangeDetectorRef);
    fb = inject(FormBuilder);
    columns = [
        { field: 'firstName', header: 'First Name' },
        { field: 'lastName', header: 'Last Name' },
        { field: 'pin', header: 'PIN' },
        { field: 'cardKey', header: 'Card' },
    ];
    availableColumns = [
        ...this.columns,
        { field: 'email', header: 'Email' },
        { field: 'employeeId', header: 'Employee ID' },
        { field: 'companyName', header: 'Company Name' }
    ];
    ngOnInit() {
        this.users = this.data;
        this.createForm();
    }
    ngOnChanges(changes) {
        this.users = this.data;
        if (changes.listOrg && this.userForm) {
            this.userForm.get('org').patchValue(changes.listOrg.currentValue);
        }
    }
    get formC() { return this.userForm.controls; }
    createForm() {
        this.userForm = this.fb.group({
            firstName: ['', [Validators.required, Validators.minLength(2)]],
            lastName: ['', [Validators.required, Validators.minLength(2)]],
            email: ['', [Validators.email]],
            employeeId: [''],
            companyName: [''],
            pin: ['', Validators.minLength(4)],
            cardKey: ['', Validators.minLength(1)],
            id: [''],
            org: [this.listOrg],
            gateUserList: [this.listId]
        }, { validators: requirePinOrCardKey() });
    }
    openNew() {
        this.userForm.reset();
        this.userDialog = true;
    }
    deleteSelectedUsers() {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete the selected users?',
            header: 'Confirm',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'reject',
            acceptLabel: 'Delete',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => {
                const userIds = this.selectedUsers.map(user => user.id);
                this.accessListService.deleteUsers(userIds, this.listId).pipe(takeUntil(this.unsubscribe$), tap(() => {
                    this.users = this.users.filter(val => !this.selectedUsers.includes(val));
                    this.selectedUsers = null;
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Users Deleted',
                        life: 3000
                    });
                }), catchError(() => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'There was an error when trying to delete the users',
                        life: 3000
                    });
                    return throwError(() => new Error('Error deleting users'));
                })).subscribe();
            }
        });
    }
    editUser(user) {
        this.userForm.reset();
        this.userForm.patchValue(user);
        this.userForm.get('org').patchValue(user?.org || this.listOrg);
        this.userDialog = true;
    }
    deleteUser(user) {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete <strong>${user.firstName} ${user.lastName} (${user.email})</strong>?`,
            header: 'Confirm',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'reject',
            acceptLabel: 'Delete',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-secondary',
            accept: async () => {
                this.accessListService.deleteUser(user.id, this.listId).pipe(takeUntil(this.unsubscribe$), tap(() => {
                    this.users = this.users.filter(val => val.id !== user.id);
                    this.selectedUsers = null;
                    this.messageService.add({
                        severity: 'success', summary: 'Successful', detail: 'User Deleted', life: 3000
                    });
                }), catchError(() => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'There was an error when trying to delete the user',
                        life: 3000
                    });
                    return throwError(() => new Error('Error deleting user'));
                })).subscribe();
            }
        });
    }
    hideDialog() {
        this.userDialog = false;
        this.userForm.reset();
        this.cdr.detectChanges();
    }
    async saveUser() {
        if (this.userForm.valid) {
            if (this.userForm.value.id) {
                this.accessListService.updateUser(this.userForm.value.id, this.listId, this.userForm.value).pipe(takeUntil(this.unsubscribe$), tap((updatedUser) => {
                    if (_.isArray(updatedUser) && updatedUser.length) {
                        updatedUser = updatedUser[0];
                    }
                    this.users[this.findIndexById(updatedUser.id)] = updatedUser;
                    this.users = [...this.users];
                    this.hideDialog();
                    this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'User Updated', life: 3000 });
                }), catchError((error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.data?.error, life: 3000 });
                    return throwError(() => new Error('Error updating user'));
                })).subscribe();
            }
            else {
                this.accessListService.addUser(this.listId, this.userForm.value).pipe(takeUntil(this.unsubscribe$), tap((createdUser) => {
                    this.users.push(createdUser);
                    this.users = [...this.users];
                    this.hideDialog();
                    this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'User Added', life: 3000 });
                }), catchError((error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.data.error, life: 3000 });
                    return throwError(() => new Error('Error adding user'));
                })).subscribe();
            }
            this.userDialog = false;
        }
    }
    findIndexById(id) {
        let index = -1;
        for (let i = 0; i < this.users.length; i++) {
            if (this.users[i].id === id) {
                index = i;
                break;
            }
        }
        return index;
    }
    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
    static propDecorators = {
        data: [{ type: Input }],
        listId: [{ type: Input }],
        listOrg: [{ type: Input }]
    };
};
AccessUsersGridComponent = __decorate([
    Component({
        selector: 'access-users-grid',
        template: __NG_CLI_RESOURCE__0,
        standalone: true,
        encapsulation: ViewEncapsulation.None,
        providers: [ConfirmationService, MessageService],
        imports: [
            CommonModule, TableModule, FormsModule, InputTextModule, ButtonModule, ConfirmPopupModule,
            ToolbarModule, ToastModule, DialogModule, ConfirmDialogModule, DropdownModule, ReactiveFormsModule, FormsModule,
            InputNumberModule, ReactiveFormsModule, CheckboxModule, MultiSelectModule, RippleModule
        ],
        styles: ["\n    "]
    })
], AccessUsersGridComponent);
export { AccessUsersGridComponent };
export function requirePinOrCardKey() {
    return (control) => {
        const pin = control.get('pin')?.value;
        const cardKey = control.get('cardKey')?.value;
        if (!pin && !cardKey) {
            return { requirePinOrCardKey: true };
        }
        return null;
    };
}
